import React, { useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import swal from "@sweetalert/with-react";
import { API } from "aws-amplify";

import { useOutsideClick, cloneObj } from "../../../../js/Helpers";
import { 
  useScheduleData, 
  useScheduleActions, 
  useConflictData
} from "../../store/magnetboardStore";
import { useReportsActions } from "../../store/reportsStore";

const MagnetboardTopBar = React.memo(({
  magnetboardOptions,
  scheduleData: { scheduleInfo, setScheduleInfo, schoolInfo },
  magnetboardActions: { pullNewSectionData, handleUpdateScheduleVersionName },
  visibleConflicts = null,
}) => {
  const {
    currentSchedule,
    editingScheduleName,
    currentScheduleVersionID,
    otherSchedulesVisible
  } = useScheduleData();

  const {
    matchPercent,
    matchPercentLoading,
    conflictsLoading,
    conflicts,
  } = useConflictData();

  const {
    setIsSaving,
    setEditingScheduleName,
    setCurrentScheduleVersionID,
    setCurrentSchedule,
    toggleOtherScheduleOptions,
    setOtherSchedulesVisible,
  } = useScheduleActions();

  const {
    addOpenReport
  } = useReportsActions();

  const other_schedules_ref = useRef();

  const showNewNameModal = (error_message = null) => {
    const options = {
      className: "swal-custom-save-schedule-name",
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: "gray-btn",
          closeModal: true,
        },
        confirm: {
          text: "Save",
          visible: true,
          className: "blue-btn",
          closeModal: false,
          value: "",
        },
      },
      content: (
        <div>
          <div>
            <FontAwesomeIcon
              className="modal-top-icon blue-text"
              icon={faCopy}
            />
            <h1 style={{ margin: "0px", textAlign: "left" }}>
              Saving New Copy
            </h1>
            <p
              className="medium-text"
              style={{ maxWidth: "300px", textAlign: "left" }}
            >
              This will create a new copy of your schedule that you can edit (to
              try to increase the match percent) without affecting your current
              copy
            </p>
            <div style={{ margin: "0px 0px 50px 0px" }}>
              <div
                className="small-text"
                style={{ margin: "20px 0px 0px 0px", textAlign: "left" }}
              >
                New Schedule Name
              </div>
              <input
                className={`add-new-schedule-name ${
                  error_message ? "error" : ""
                }`}
                defaultValue=""
                onChange={(e) => swal.setActionValue(e.target.value)}
              />
            </div>
          </div>
          {error_message && (
            <div className="red-text small-text">{error_message}</div>
          )}
        </div>
      ),
    };

    return swal(options).then((schedule_name) => {
      if (schedule_name === "") {
        swal.stopLoading();
        swal.close();
        return showNewNameModal("You need to input a name for your schedule!");
      } else if (!schedule_name) {
        swal.stopLoading();
        swal.close();
        return false;
      } else {
        swal.close();
        return schedule_name;
      }
    });
  };

  const saveSchedule = async (
    save_type = "new",
    schedule_name = currentSchedule.schedule_name
  ) => {
    if (save_type === "new") schedule_name = await showNewNameModal();

    if (schedule_name) {
      if (save_type === "schedule_name") {
        if (!handleUpdateScheduleVersionName) return;

        setIsSaving(true);

        try {
          const response = await handleUpdateScheduleVersionName({
            schedule_version_id: currentScheduleVersionID,
            schedule_name,
          });

          if (response && response.success) {
            setCurrentSchedule({
              ...currentSchedule,
              schedule_name: response.schedule_name,
            });
            setEditingScheduleName(!editingScheduleName);
          }
        } finally {
          setIsSaving(false);
        }

        return;
      }

      setIsSaving(true);

      const swal_options = {
        className: "swal-custom-save-schedule-name",
        button: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
        content: (
          <div className="mb-saving-container">
            <h1 className="mb-saving-heading">Your schedule is saving</h1>
            <p style={{ margin: "0px 0px 30px 0px" }}>
              Please wait, your new schedule copy is being created.
            </p>
            <div
              className="dark-gray-text medium-text"
              style={{ marginBottom: "10px" }}
            >
              Copying...
            </div>
            <div className="mb-saving-inner-container">
              <div className="mb-saving-row">
                <div>Section Placements</div>
                <div className="mb-saving-progress progress-bar-container">
                  <div
                    id="mb-saving-section-progress"
                    className="progress-bar-inner-container"
                  ></div>
                </div>
              </div>
              <div className="mb-saving-row">
                <div>Teacher Placements</div>
                <div className="mb-saving-progress progress-bar-container">
                  <div
                    id="mb-saving-teacher-progress"
                    className="progress-bar-inner-container"
                  ></div>
                </div>
              </div>
              <div className="mb-saving-row">
                <div>Classroom Placements</div>
                <div className="mb-saving-progress progress-bar-container">
                  <div
                    id="mb-saving-classroom-progress"
                    className="progress-bar-inner-container"
                  ></div>
                </div>
              </div>
              <div className="mb-saving-row">
                <div>Student Placements</div>
                <div className="mb-saving-progress progress-bar-container">
                  <div
                    id="mb-saving-student-progress"
                    className="progress-bar-inner-container"
                  ></div>
                </div>
              </div>
              <div className="mb-saving-row">
                <div>Saving Copy</div>
                <div className="mb-saving-progress progress-bar-container">
                  <div
                    id="mb-saving-copying-progress"
                    className="progress-bar-inner-container"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ),
      };

      swal(swal_options);

      setTimeout(function () {
        swal.close();
        setIsSaving(false);
      }, 25000);

      const data = {
        school_id: schoolInfo.school_id,
        save_type: save_type,
        schedule_version_id: currentScheduleVersionID,
        schedule_name: schedule_name,
        match_percent: matchPercent,
      };

      const apiName = process.env.REACT_APP_ENDPOINT_NAME;
      const url = "/save-schedule";
      const myInit = {
        // OPTIONAL
        response: true,
        body: JSON.stringify(data),
      };

      try {
        const response = await API.post(apiName, url, myInit);
        const response_data = response.data;

        const new_schedule_version_id = response_data.new_schedule_version_id;
        const new_schedule_name = response_data.new_schedule_name;
        const new_time_stamp = response_data.new_time_stamp;

        const old_schedule_index = scheduleInfo.schedules.findIndex(
          (schedule) =>
            schedule.schedule_version_id === currentScheduleVersionID
        );
        let new_schedule_info = cloneObj(
          scheduleInfo.schedules[old_schedule_index]
        );

        if (save_type === "new") {
          const old_schedule_info = scheduleInfo.schedules[old_schedule_index];

          old_schedule_info.current_bool = "0";

          new_schedule_info.schedule_version_id = new_schedule_version_id;
          new_schedule_info.schedule_name = new_schedule_name;
          new_schedule_info.time_stamp = new_time_stamp;
          new_schedule_info.current_bool = "1";

          scheduleInfo.schedules.push(new_schedule_info);
        }

        setCurrentScheduleVersionID(new_schedule_version_id);
        setCurrentSchedule({ ...new_schedule_info });
        setScheduleInfo({ ...scheduleInfo });
      } catch (e) {
        const response_data = e && e.response ? e.response.data : null;

        if(response_data && response_data.msg === 'Schedule versions can only be duplicated in current year')
        {
          swal({
            title: "Sorry",
            text: "You can only duplicate schedule versions in the current scheduling year.",
            icon: "warning"
          });
        }
        else if(response_data && response_data.msg === 'Schedule version limit reached')
        {
          const max_schedules_allowed = (response_data.num_schedules_allowed) ? parseInt(response_data.num_schedules_allowed, 10) : 5;
          swal({
            title: "Sorry",
            text: `Sorry you cannot have more than ${max_schedules_allowed} schedule versions in a single year. If you'd like to create a new version, please delete one of your existing ones first.`,
            icon: "warning"
          });
        }
        else
        {
          swal({
            title: "Duplicate failed",
            text: "The schedule could not be duplicated. Please try again.",
            icon: "error"
          });
        }
      }
    }
  };

  const submitScheduleName = () => {
    const schedule_name_input = document.getElementById(
      "mb-schedule-name-input"
    );
    const new_schedule_name = schedule_name_input.value;

    if (!new_schedule_name) {
      schedule_name_input.classList.add("error");
      return false;
    }

    const save_type = "schedule_name";
    saveSchedule(save_type, new_schedule_name);
  };

  useOutsideClick(other_schedules_ref, () => {
    setOtherSchedulesVisible(false);
  });

  const schedules_for_this_year = useMemo(() => 
    "schedules" in scheduleInfo
      ? scheduleInfo.schedules.filter(
          (schedule) => schedule.schedule_year_id === scheduleInfo.current_schedule_year_id
        )
      : [],
    [scheduleInfo]
  );
  const conflictsForCounts = Array.isArray(visibleConflicts) ? visibleConflicts : conflicts;
  const num_conflicts = useMemo(() => conflictsForCounts.filter(conflict => {
    const conflict_level = conflict.level || (conflict.hard_conflict === '1' ? 'conflict' : 'warning');
    return conflict_level === 'conflict' && conflict.ignored !== true && conflict.is_deleted !== '1';
  }).length, [conflictsForCounts]);
	const num_warnings = useMemo(() => conflictsForCounts.filter(conflict => {
    const conflict_level = conflict.level || (conflict.hard_conflict === '1' ? 'conflict' : 'warning');
    return conflict_level === 'warning' && conflict.ignored !== true && conflict.is_deleted !== '1';
  }).length, [conflictsForCounts]);

  return (
    <div className="mb-top-bar-row">
      <div>
        <img
          className="mb-match-img"
          src={require("../../../../images/logo-dark.png")}
          alt="Edario Logo"
        />
      </div>
      <div className={`mb-schedule-name-container ${magnetboardOptions.allow_schedule_name_editing ? "" : "mb-schedule-name-container-readonly"}`}>
        <div
          className="mb-top-bar-label"
          style={{ margin: "0px 0px 0px 12px" }}
        >
          {!magnetboardOptions.current_department
            ? "Schedule Name"
            : "Department"}
        </div>
        {!editingScheduleName && (
          <>
            {!magnetboardOptions.current_department ? (
              <h1
                className={`mb-schedule-name ${magnetboardOptions.allow_schedule_name_editing ? "" : "mb-schedule-name-no-hover"}`}
                onClick={
                  magnetboardOptions.allow_schedule_name_editing
                    ? () => setEditingScheduleName(!editingScheduleName)
                    : null
                }
              >
                {currentSchedule.schedule_name}
              </h1>
            ) : (
              <h1
                className="mb-schedule-name mb-schedule-name-no-hover"
                style={{ border: "none", textAlign: "left" }}
              >
                {magnetboardOptions.current_department} Schedule
              </h1>
            )}
          </>
        )}
        {editingScheduleName && (
          <div className="mb-editing-schedule-name-container">
            <input
              id="mb-schedule-name-input"
              defaultValue={currentSchedule.schedule_name}
            />
            <FontAwesomeIcon
              className="mb-editing-schedule-name-confirm checkmark-submit"
              icon={faCheck}
              onClick={(e) => submitScheduleName(e)}
            />
            <FontAwesomeIcon
              className="mb-editing-schedule-name-cancel x-cancel"
              icon={faTimes}
              onClick={() => setEditingScheduleName(!editingScheduleName)}
            />
          </div>
        )}
        <div ref={other_schedules_ref}>
          {schedules_for_this_year.length > 1 && (
            <div
              className="mb-other-schedules-icon click-restricted"
              onClick={toggleOtherScheduleOptions}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </div>
          )}
          {otherSchedulesVisible && (
            <div className="mb-other-schedules-container dropdown-options-container">
              <h5>Saved Schedules</h5>
              {schedules_for_this_year.map((schedule_info, i) => {
                const schedule_selected =
                  schedule_info.current_bool === "1" ? true : false;

                const t = schedule_info.time_stamp.split(/[- :]/);
                const d = new Date(
                  Date.UTC(t[0], t[1] - 1, t[2], t[3], t[4], t[5])
                );
                const datestring =
                  ("0" + (d.getMonth() + 1)).slice(-2) +
                  "-" +
                  ("0" + d.getDate()).slice(-2) +
                  "-" +
                  d.getFullYear();

                return (
                  <div
                    className={`mb-other-schedules-option ${
                      schedule_selected
                        ? "mb-other-schedules-option-selected"
                        : ""
                    }`}
                    key={i}
                    onClick={
                      schedule_selected
                        ? () => {}
                        : () =>
                            pullNewSectionData(
                              schedule_info.schedule_version_id
                            )
                    }
                  >
                    <img
                      className="mb-other-schedules-logo"
                      src={
                        schedule_selected
                          ? require("../../../../images/logo-white.png")
                          : require("../../../../images/logo-dark.png")
                      }
                      alt="Edario Logo"
                    />
                    <div>
                      <div
                        className={`extra-small-text ${
                          schedule_selected ? "gray-text" : "dark-gray-text"
                        }`}
                      >
                        Schedule Name
                      </div>
                      <div>{schedule_info.schedule_name}</div>
                    </div>
                    {magnetboardOptions.show_match_percent && (
                      <div>
                        <div
                          className={`extra-small-text ${
                            schedule_selected ? "gray-text" : "dark-gray-text"
                          }`}
                        >
                          Match
                        </div>
                        <div>{schedule_info.match_percent}%</div>
                      </div>
                    )}
                    <div>
                      <div
                        className={`extra-small-text ${
                          schedule_selected ? "gray-text" : "dark-gray-text"
                        }`}
                      >
                        Last Saved
                      </div>
                      <div>{datestring}</div>
                    </div>
                    <div>
                      <FontAwesomeIcon
                        className={`mb-other-schedule-check-mark ${
                          schedule_selected ? "green-text" : "white-text"
                        }`}
                        style={{ marginTop: "10px", fontSize: "20px" }}
                        icon={faCheckCircle}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="mb-schedule-stats-container">
        <div className={`mb-match-percent-container ${!magnetboardOptions.show_conflicts ? "mb-match-percent-container-right" : ""}`}>
          {magnetboardOptions.show_match_percent && (
            <>
              <div className="mb-top-bar-label">Match</div>
              <h1 className="mb-match-percent">
                {matchPercentLoading ? (
                  <div style={{ width: "128px", height: "60px" }}>
                    <img
                      src={require("../../../../images/balls.gif")}
                      alt="loading..."
                      style={{ height: "50px", textAlign: "left" }}
                    />
                  </div>
                ) : (
                  `${matchPercent}%`
                )}
              </h1>
            </>
          )}
        </div>
        {magnetboardOptions.show_conflicts && (
          <div className="mb-conflict-container">
              <div className="mb-conflict-inner-row">
                Conflicts{" "}
                <span className="mb-conflict-counter">
                  {conflictsLoading ? (
                    <img
                      src={require("../../../../images/balls.gif")}
                      alt="loading..."
                      style={{ height: "20px" }}
                    />
                  ) : (
                    num_conflicts
                  )}
                </span>
              </div>
              <div className="mb-conflict-inner-row">
                Warnings{" "}
                <span className="mb-conflict-counter">
                  {conflictsLoading ? (
                    <img
                      src={require("../../../../images/balls.gif")}
                      alt="loading..."
                      style={{ height: "20px" }}
                    />
                  ) : (
                    num_warnings
                  )}
                </span>
              </div>
              {!conflictsLoading && (
                <div
                  className="blue-link small-text"
                  style={{ marginTop: "5px", textAlign: "right" }}
                  onClick={() => addOpenReport("conflicts")}
                >
                  See all
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
});

export default MagnetboardTopBar;
